import { Component, OnInit, Input } from '@angular/core';
import { BehaviorSubject, finalize, of, Subscription, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ServiceChangeRequestService } from 'src/app/core/services/service-change-request.service';
import {
  IHttpPagedResponse,
  IUiAlertContent,
  ToastService,
  TUiAlertTypes,
} from '@irembo-andela/irembogov3-common';
import { ICertificateTemplate } from 'src/app/core/models/certificate-template-model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { ICreateCertificateRequest } from 'src/app/core/models/http/create-certificate-request.model';
import { Router } from '@angular/router';

interface IGetListParamsFields {
  page: number;
  size: number;
  sort?: string;
  sortDirection?: string;
  organizationId?: string;
}

interface CertificateModel {
  organizationId: string;
  templateType: string;
  templateName: string;
  templateLanguage: string;
  template: File | null;
}

type PaneForView = 'ADD_CERTIFICATE';

@Component({
  selector: 'irembogov-irembogov-certificate-list',
  templateUrl: './irembogov-certificate-list.component.html',
  styleUrls: ['./irembogov-certificate-list.component.scss'],
})
export class IrembogovCertificateListComponent implements OnInit {
  @Input() organizationId?: string;
  certificateTemplates: ICertificateTemplate[] = [];
  selectedTemplate!: ICertificateTemplate;

  certificateForm!: FormGroup;
  isUploadingCertificate = false;

  isPaneOPen = false;
  sidePaneType!: PaneForView;

  showFormErrorMessage = false;
  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };

  collectionSize = 0;
  pageSize = 10;
  page = 0;
  isLoading = false;

  form = new FormGroup({});
  model: CertificateModel = {
    organizationId: '',
    templateType: 'JRXML',
    templateName: '',
    templateLanguage: '',
    template: null,
  };
  isSubmitting = false;

  defaultParamsFields: IGetListParamsFields = {
    page: 0,
    size: this.pageSize,
    sortDirection: 'DESC',
  };

  sortFieldOptions = [
    { label: 'Date Created - DESC', key: 'dateCreated,DESC' },
    { label: 'Date Created - ASC', key: 'dateCreated,ASC' },
  ];

  getQueryParamsSubscription$!: Subscription;

  formlyFormOptions: Record<string, unknown> = {
    formState: { hideBuilderWrapper: true },
  };

  _filterObject: BehaviorSubject<IGetListParamsFields> =
    new BehaviorSubject<IGetListParamsFields>(this.defaultParamsFields);

  constructor(
    private toastService: ToastService,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private organizationService: OrganizationService,
    private fb: FormBuilder,
    private router: Router
  ) {}

  fields: FormlyFieldConfig[] = [
    {
      key: 'organizationId',
      type: 'input',
      className: 'col-12',
      props: {
        label: 'Organization ID',
        required: true,
        readonly: true,
      },
      defaultValue: this.organizationId,
    },
    {
      key: 'templateType',
      type: 'input',
      className: 'col-12',
      props: {
        label: 'Template Type',
        required: true,
        readonly: true,
      },
      defaultValue: 'JRXML',
    },
    {
      key: 'templateName',
      type: 'input',
      className: 'col-12',
      props: {
        label: 'Template Name',
        required: true,
        placeholder: 'Enter template name',
      },
    },
    {
      key: 'templateLanguage',
      type: 'customdropdownpaginated',
      className: 'col-12',
      props: {
        label: 'Certificate Language',
        required: true,
        placeholder: 'Select Certificate Language',
        bindLabel: 'name',
        bindValue: 'locale',
        multiple: false,
        options: [],
        useBaseUrl: true,
        dataset: {
          url: '/admin/v1/languages',
          params: { size: 10, page: 0 },
          dataField: 'data.content',
          pagination: 'true',
          searching: 'true',
          searchField: 'name',
        },
      },
      validation: {
        messages: {
          required: 'Error message for required',
        },
      },
    },
    {
      key: 'template',
      type: 'formly-file-input',
      className: 'col-12',
      props: {
        label: 'Template File',
        required: true,
        allowedFormats: ['jrxml'],
      },
    },
  ];
  ngOnInit(): void {
    if (this.organizationId) this.model.organizationId = this.organizationId;
    this.getCertificateTemplates(this.defaultParamsFields);

    this.getQueryParamsSubscription$ = this._filterObject
      .asObservable()
      .pipe(
        switchMap((paramsFields: IGetListParamsFields) => {
          this.getCertificateTemplates(paramsFields);
          return of();
        })
      )
      .subscribe();
  }

  updateTemplateList(keyPair: Partial<IGetListParamsFields>) {
    this._filterObject.next({
      ...this._filterObject.getValue(),
      ...keyPair,
    });
  }

  getCertificateTemplates(params: IGetListParamsFields) {
    this.isLoading = true;
    let queryParams = `page=${params.page}&size=${params.size}`;

    if (params.sort) {
      queryParams += `&sort=${params.sort}`;
    }
    if (params.sortDirection) {
      queryParams += `&sortDirection=${params.sortDirection}`;
    }

    queryParams += `&organizationId=${this.organizationId}`;

    this.serviceChangeRequestService
      .getCertificateTemplates(queryParams)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: IHttpPagedResponse<ICertificateTemplate>) => {
          this.certificateTemplates = response.data.content;
          this.collectionSize = response.data.totalElements;
        },
        error: (err: HttpErrorResponse) => {
          const errorMessage = err.error.responseMessage;
          this.updateFormErrorMessageContent(
            true,
            'danger',
            'Updating service category failed',
            errorMessage
          );
        },
      });
  }

  onSelectTemplate(template: ICertificateTemplate) {
    this.selectedTemplate = template;
  }

  copyTemplateCode(code: string) {
    navigator.clipboard.writeText(code).then(() => {
      this.toastService.show({
        body: 'Template code copied to clipboard',
        type: 'success',
      });
    });
  }

  openPaneForCertificate() {
    this.updateToggleState(true);
  }

  updateToggleState(openState: boolean) {
    this.isPaneOPen = openState;
  }

  onFileSelect(event: any) {
    const file = event.target.files[0];
    this.model['template'] = file;
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.isUploadingCertificate = true;

      const formData: ICreateCertificateRequest = {
        ...this.model,
        organizationId: this.model.organizationId,
      };

      this.organizationService
        .createCertificate(formData)
        .pipe(finalize(() => (this.isUploadingCertificate = false)))
        .subscribe({
          next: () => {
            this.updateTemplateList(this.defaultParamsFields);

            this.form.reset();
            this.model = {
              organizationId: this.organizationId || '',
              templateType: 'JRXML',
              templateName: '',
              templateLanguage: '',
              template: null,
            };

            this.updateTemplateList({
              page: 0,
              size: this.pageSize,
              sortDirection: 'ASC',
            });

            this.toastService.show({
              body: 'Certificate Created successfully',
              type: 'success',
            });
            this.updateToggleState(false);
          },
          error: (err: HttpErrorResponse) => {
            const errorMessage = err.error.responseMessage;
            this.updateFormErrorMessageContent(
              true,
              'danger',
              'Creating Certificate failed',
              errorMessage
            );
          },
        });
    }
  }

  updateFormErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title = '',
    message = ''
  ) {
    this.showFormErrorMessage = show;
    this.formAlertContent = { type, title, message };
  }

  private cleanUrl(url: string): string {
    return url.split('#')[0];
  }

  navigateToDesignNew(): void {
    const currentUrl = this.cleanUrl(this.router.url);
    const url = `${window.location.origin}${currentUrl}/certificate/new`;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.focus();
  }

  navigateToPreview(templateCode: string): void {
    const currentUrl = this.cleanUrl(this.router.url);
    const url = `${window.location.origin}${currentUrl}/certificate/${templateCode}/preview`;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.focus();
  }

  navigateToEdit(templateCode: string): void {
    const currentUrl = this.cleanUrl(this.router.url);
    const url = `${window.location.origin}${currentUrl}/certificate/${templateCode}/design`;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.focus();
  }

  canEdit(template: ICertificateTemplate): boolean {
    return template.type === 'HTML';
  }

  onRefresh() {
    this.getCertificateTemplates(this.defaultParamsFields);
  }
}
